import axios from 'axios';
import axiosInstance from '../../services/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Keep marketplace API compatible with both auth cookie and localStorage token flows.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Build query params for list endpoint from filters and pagination
 * @param {Object} params - { page, limit, search, category, platform, isActive, followersMin, followersMax, priceMax, language, gender, location, contentType, isVerified }
 * @returns {Object} - query params object
 */
export const buildListParams = (params = {}) => {
  const {
    page = 1,
    limit = 20,
    search = '',
    category = '',
    platform = '',
    isActive = true,
    followersMin,
    followersMax,
    priceMax,
    language = '',
    gender = '',
    location = '',
    contentType = '',
    isVerified,
  } = params;

  const query = {
    page: Number(page) || 1,
    limit: Number(limit) || 20,
    isActive: Boolean(isActive),
  };

  if (search && String(search).trim()) query.search = String(search).trim();
  if (category && String(category).trim()) query.category = String(category).trim();
  if (platform && String(platform).trim()) query.platform = String(platform).trim();
  if (followersMin != null && !Number.isNaN(Number(followersMin))) query.followersMin = Number(followersMin);
  if (followersMax != null && !Number.isNaN(Number(followersMax))) query.followersMax = Number(followersMax);
  if (priceMax != null && priceMax !== '' && !Number.isNaN(Number(priceMax))) query.priceMax = Number(priceMax);
  if (language && String(language).trim()) query.language = String(language).trim();
  if (gender && String(gender).trim()) query.gender = String(gender).trim();
  if (location && String(location).trim()) query.location = String(location).trim();
  if (contentType && String(contentType).trim()) query.contentType = String(contentType).trim();
  if (typeof isVerified === 'boolean') query.isVerified = isVerified;

  return query;
};

/**
 * Fetch paginated marketplace influencers (public list)
 * @param {Object} params - see buildListParams
 * @returns {Promise<{ data: { influencers, pagination }, success }>}
 */
export const getInfluencers = async (params = {}) => {
  const query = buildListParams(params);
  // Public marketplace should go through the Brands service via the gateway,
  // not hit the Admin service directly.
  const response = await api.get('/company/marketplace/influencers', { params: query });
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Failed to fetch influencers');
  }
  return response.data;
};

/**
 * Fetch single influencer by username (public, active only)
 * @param {string} username
 * @returns {Promise<{ data: influencer, success }>}
 */
export const getInfluencerByUsername = async (username) => {
  if (!username || !String(username).trim()) {
    return null;
  }
  try {
    // Public marketplace profile fetch via Brands service
    const response = await api.get(
      `/company/marketplace/influencers/by-username/${encodeURIComponent(
        String(username).trim()
      )}`
    );
    if (!response.data?.success || !response.data?.data) {
      return null;
    }
    return response.data.data;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
};

export const getSimilarInfluencers = async (username) => {
  if (!username || !String(username).trim()) {
    return null;
  }
  const response = await api.get(
    `/company/marketplace/influencers/${encodeURIComponent(
      String(username).trim()
    )}/similar`
  );
  if (!response.data?.success || !response.data?.data) {
    return null;
  }
  return response.data.data;
};

export const getInfluencerContactInfo = async (username) => {
  if(!username || !String(username).trim()){
    return null;
  }
  const response = await api.get(
    `/company/marketplace/influencers/${encodeURIComponent(
      String(username).trim()
    )}/contact-info`
  );
  if (!response.data?.success || !response.data?.data) {
    return null;
  }
  return response.data.data;
}

export const getActiveCampaignsForInvite = async () => {
  const response = await api.get('/company/marketplace/campaigns/active');
  if (!response.data?.success || !Array.isArray(response.data?.data)) {
    throw new Error(response.data?.message || 'Failed to fetch active campaigns');
  }
  return response.data.data;
};

/**
 * Pipeline items for a campaign (same auth as shortlist). Used to avoid duplicate shortlists.
 */
export const listPipelineItemsForCampaign = async (campaignId) => {
  const cleanCampaignId = String(campaignId || '').trim();
  if (!cleanCampaignId) {
    throw new Error('campaignId is required');
  }
  const response = await api.get('/company/pipeline/items', {
    params: { campaignId: cleanCampaignId },
  });
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Failed to load pipeline');
  }
  return Array.isArray(response.data?.data) ? response.data.data : [];
};

const parseEngagementRate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(String(value).replace('%', '').trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const getInfluencerEngagementRate = (influencer) => {
  const instagramAnalytics =
    influencer?.analytics?.Instagram || influencer?.analytics?.instagram || {};

  const candidates = [
    instagramAnalytics.engagementRate,
    instagramAnalytics.engagement,
    influencer?.engagementRate,
  ];

  for (const candidate of candidates) {
    const parsed = parseEngagementRate(candidate);
    if (parsed !== null) return parsed;
  }

  return 0;
};

const normalizePlatform = (value) => {
  const clean = String(value || '').trim().toLowerCase();
  if (clean === 'instagram') return 'Instagram';
  if (clean === 'youtube') return 'YouTube';
  return clean ? clean[0].toUpperCase() + clean.slice(1) : '';
};

export const inviteInfluencerToCampaign = async ({
  influencerId,
  campaignId,
  influencer,
  selectedPackage,
}) => {
  const cleanInfluencerId = String(influencerId || '').trim();
  const cleanCampaignId = String(campaignId || '').trim();

  if (!cleanInfluencerId || !cleanCampaignId) {
    throw new Error('Influencer and campaign are required');
  }

  // Enterprise pipeline flow: adds influencer to pipeline "Shortlisted"
  // without sending any invitation email/notification.
  const engagementRate = getInfluencerEngagementRate(influencer);
  const selectedPackagePrice = Number(selectedPackage?.price);
  const effectiveRate = Number.isFinite(selectedPackagePrice) ? selectedPackagePrice : 0;
  const selectedPlatform = normalizePlatform(selectedPackage?.platform);
  const selectedNiche = String(selectedPackage?.niche || "").trim();
  const normalizedPlatforms = selectedPlatform
    ? [selectedPlatform]
    : Array.isArray(influencer?.platforms)
      ? influencer.platforms.map(normalizePlatform).filter(Boolean)
      : [];
  const normalizedNiches =
    selectedNiche && !Array.isArray(influencer?.niches)
      ? [selectedNiche]
      : Array.isArray(influencer?.niches)
        ? influencer.niches
        : Array.isArray(influencer?.categories)
          ? influencer.categories
          : selectedNiche
            ? [selectedNiche]
            : [];

  const response = await api.post('/company/pipeline/items/shortlist', {
    campaignId: cleanCampaignId,
    influencerId: cleanInfluencerId,
    username: String(influencer?.username || influencer?.handle || '').trim(),
    snapshot: {
      name: influencer?.name || '',
      username: influencer?.username || influencer?.handle || '',
      avatarUrl: influencer?.profileImage || influencer?.avatarUrl || '',
      niches: normalizedNiches,
      platforms: normalizedPlatforms,
      followers: influencer?.followers || influencer?.followerCount || 0,
      engagementRate,
      rate: effectiveRate,
      selectedPackage: {
        id: String(selectedPackage?.id || '').trim(),
        title: String(selectedPackage?.title || '').trim(),
        platform: selectedPlatform,
        niche: selectedNiche,
        currencySymbol: String(selectedPackage?.currencySymbol || 'INR ').trim() || 'INR ',
        price: effectiveRate,
      },
    },
  });

  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Failed to add influencer to pipeline');
  }

  return response.data;
};

export const getSavedInfluencerStatus = async ({ influencerId, username }) => {
  const params = {};
  if (influencerId && String(influencerId).trim()) {
    params.influencerId = String(influencerId).trim();
  }
  if (username && String(username).trim()) {
    params.username = String(username).trim();
  }

  const response = await api.get('/company/marketplace/saved-influencers/status', {
    params,
  });

  if (!response.data?.success || typeof response.data?.data?.isSaved !== 'boolean') {
    throw new Error(response.data?.message || 'Failed to fetch saved influencer status');
  }

  return response.data.data.isSaved;
};

export const saveInfluencer = async ({ influencerId, username }) => {
  const payload = {
    influencerId: String(influencerId || '').trim(),
    username: String(username || '').trim(),
  };

  const response = await api.post('/company/marketplace/saved-influencers', payload);
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Failed to save influencer');
  }
  return response.data.data || { isSaved: true };
};

export const unsaveInfluencer = async ({ influencerId, username }) => {
  const response = await api.delete('/company/marketplace/saved-influencers', {
    data: {
      influencerId: String(influencerId || '').trim(),
      username: String(username || '').trim(),
    },
  });
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Failed to unsave influencer');
  }
  return response.data.data || { isSaved: false };
};

export const getInfluencerReviews = async (username, params = {}) => {
  if (!username || !String(username).trim()) {
    return {
      reviews: [],
      totalReviews: 0,
      averageRating: 0,
      ratingBreakdown: [5, 4, 3, 2, 1].map((rating) => ({
        rating,
        count: 0,
      })),
      pagination: {
        page: 1,
        limit: 10,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      },
    };
  }

  const response = await axiosInstance.get(
    `/company/influencers/${encodeURIComponent(String(username).trim())}/reviews`,
    { params }
  );

  if (!response.data?.success || !response.data?.data) {
    throw new Error(response.data?.message || 'Failed to fetch reviews');
  }

  return response.data.data;
};

export const createInfluencerReview = async (payload) => {
  const response = await axiosInstance.post('/company/influencers/reviews', payload);

  if (!response.data?.success || !response.data?.data) {
    throw new Error(response.data?.message || 'Failed to create review');
  }

  return response.data.data;
};

export const updateInfluencerReview = async (reviewId, payload) => {
  const response = await axiosInstance.patch(
    `/company/influencers/reviews/${encodeURIComponent(String(reviewId).trim())}`,
    payload
  );

  if (!response.data?.success || !response.data?.data) {
    throw new Error(response.data?.message || 'Failed to update review');
  }

  return response.data.data;
};

export const deleteInfluencerReview = async (reviewId) => {
  const response = await axiosInstance.delete(
    `/company/influencers/reviews/${encodeURIComponent(String(reviewId).trim())}`
  );

  if (!response.data?.success || !response.data?.data) {
    throw new Error(response.data?.message || 'Failed to delete review');
  }

  return response.data.data;
};

export const marketplaceApi = {
  buildListParams,
  getInfluencers,
  getInfluencerByUsername,
  getSimilarInfluencers,
  getInfluencerContactInfo,
  getActiveCampaignsForInvite,
  listPipelineItemsForCampaign,
  inviteInfluencerToCampaign,
  getSavedInfluencerStatus,
  saveInfluencer,
  unsaveInfluencer,
  getInfluencerReviews,
  createInfluencerReview,
  updateInfluencerReview,
  deleteInfluencerReview,
};

export default marketplaceApi;
